import { useState, useEffect, useRef, type CSSProperties } from "react";
import type { Task } from "../../../lib/types";
import { useCreateTask } from "../../hooks/use-task-mutations";
import { showToast } from "../primitives";
import { modalOverlay, modalBox, fieldStyle, labelStyle, inputStyle } from "./task-modal-shared";

interface NewTaskModalProps {
  open: boolean;
  onClose: () => void;
  /** Agent node the task is hosted on. */
  nodeId: string;
  onCreated?: (task: Task) => void;
}

const BODY_LIMIT = 2000;

/**
 * Create a task on an agent (Sprint 26.0 — the one task noun). A task carries
 * its own document (`body`); new tasks land in the inbox by default for triage.
 */
export function NewTaskModal({ open, onClose, nodeId, onCreated }: NewTaskModalProps) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [dueBy, setDueBy] = useState("");
  const titleRef = useRef<HTMLInputElement>(null);
  const createTask = useCreateTask();

  useEffect(() => {
    if (open) {
      setTitle("");
      setBody("");
      setDueBy("");
      setTimeout(() => titleRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    createTask.mutate(
      {
        node_id: nodeId,
        title: title.trim(),
        body: body.trim() || undefined,
        due_by: dueBy || undefined,
      },
      {
        onSuccess: ({ task }) => {
          showToast(`Task '${task.title}' created`);
          onCreated?.(task);
          onClose();
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : "Something went wrong";
          showToast(`Couldn't create task: ${msg}`, "error", true);
        },
      },
    );
  }

  const iconBox: CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: "var(--border-radius-md)",
    background: "var(--color-background-secondary)",
    border: "var(--kuvio-border-default)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--color-text-secondary)",
    fontSize: 20,
    flexShrink: 0,
  };

  return (
    <div style={modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalBox} role="dialog" aria-label="New task">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={iconBox}>
            <i className="ti ti-checklist" aria-hidden="true" />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>New task</div>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>A unit of work the agent owns</div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Title</label>
            <input
              ref={titleRef}
              type="text"
              style={inputStyle}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to be done?"
              maxLength={200}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Details</label>
            <textarea
              style={{ ...inputStyle, minHeight: 96, resize: "vertical" }}
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, BODY_LIMIT))}
              placeholder="Optional — the task document. Markdown supported."
              maxLength={BODY_LIMIT}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Due date</label>
            <input
              type="date"
              style={inputStyle}
              value={dueBy}
              onChange={(e) => setDueBy(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ fontSize: 13, padding: "8px 14px" }}>
              Cancel
            </button>
            <button
              type="submit"
              data-variant="primary"
              disabled={!title.trim() || createTask.isPending}
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              {createTask.isPending ? "Creating..." : "Create task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
